// =============================================================
// קובץ: C:\gocode\projects\cartax\git\cartax_fe\src\services\enduser\epaService.ts
// =============================================================

import { API_BASE_URL } from '../../config/api';

// ============== Interfaces ==============

export interface EpaVehicleData {
  model_year: number;
  make: string;
  model: string;
  fuel_type: string;
  ftp_thc: number;
  ftp_co: number;
  ftp_co2: number;
  ftp_nox: number;
  ftp_pm: number;
  hwy_thc: number;
  hwy_co: number;
  hwy_co2: number;
  hwy_nox: number;
  hwy_pm: number;
}

export interface ApiResponse<T> {
  res: 'OK' | 'NOK';
  msg: string;
  data: T;
}

// ============== Helper Functions ==============

const createFormData = (data: Record<string, any>): FormData => {
  const formData = new FormData();

  const sessionToken = localStorage.getItem('enduser_session_id');
  if (sessionToken) {
    formData.append('session_token', sessionToken);
  }

  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value.toString());
    }
  });
  return formData;
};

// ============== Service Class ==============

class EpaService {
  /**
   * קבלת רשימת שנים זמינות
   */
  async getYears(): Promise<number[]> {
    try {
      const formData = createFormData({});

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/epa_get_years`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.res === 'NOK') {
        throw new Error(result.msg || 'Failed to fetch EPA years');
      }

      return result.data.years || [];

    } catch (error) {
      console.error('EPA years service error:', error);
      throw error;
    }
  }

  /**
   * קבלת רשימת יצרנים לפי שנה
   */
  async getMakes(year: number): Promise<string[]> {
    try {
      const formData = createFormData({ year });

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/epa_get_makes`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.res === 'NOK') {
        throw new Error(result.msg || 'Failed to fetch EPA makes');
      }

      return result.data.makes || [];

    } catch (error) {
      console.error('EPA makes service error:', error);
      throw error;
    }
  }

  /**
   * קבלת רשימת דגמים לפי שנה ויצרן
   */
  async getModels(year: number, make: string): Promise<string[]> {
    try {
      const formData = createFormData({ year, make });

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/epa_get_models`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.res === 'NOK') {
        throw new Error(result.msg || 'Failed to fetch EPA models');
      }

      return result.data.models || [];

    } catch (error) {
      console.error('EPA models service error:', error);
      throw error;
    }
  }

  /**
   * קבלת רשימת סוגי דלק לפי שנה, יצרן ודגם
   */
  async getFuels(year: number, make: string, model: string): Promise<string[]> {
    try {
      const formData = createFormData({ year, make, model });

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/epa_get_fuels`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.res === 'NOK') {
        throw new Error(result.msg || 'Failed to fetch EPA fuels');
      }

      return result.data.fuels || [];

    } catch (error) {
      console.error('EPA fuels service error:', error);
      throw error;
    }
  }

  /**
   * קבלת נתוני זיהום מלאים לרכב ספציפי
   */
  async getVehicleData(
    year: number,
    make: string,
    model: string,
    fuelType: string
  ): Promise<EpaVehicleData | null> {
    try {
      const formData = createFormData({
        year,
        make,
        model,
        fuel_type: fuelType
      });

      const response = await fetch(`${API_BASE_URL}/Api/service/enduser/epa_get_vehicle_data`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.res === 'NOK') {
        throw new Error(result.msg || 'Failed to fetch EPA vehicle data');
      }

      return result.data.vehicle || null;

    } catch (error) {
      console.error('EPA vehicle data service error:', error);
      throw error;
    }
  }
}

export const epaService = new EpaService();